import { Grid2 as Grid, Box, Stack } from "@mui/material"
import { SectionTitle } from "../../components/SectionTitle"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useNavigate } from "react-router-dom"
import { useFondo, useFondoUpdate, useFondoHistory, useFondoDelete, useFondoSetAllegati } from "../../hooks/fondi"
import { useSnackbarMessage } from "../../hooks/snackbar"
import { useDesiredHeight } from "../../hooks/measuring"
import { DataField, DataRow } from "../../components/DataField"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { joinNomeCognome } from "../../utils/text"
import ChiaviAccessoDisplay from "../../components/ChiaviAccessoDisplay"
import RecordInfo from "../../components/RecordInfo"
import DeleteBar from "../../components/DeleteBar"
import DialogConfirm from "../../components/DialogConfirm"
import DialogEliminaFondoConSupporti from "../../components/DialogEliminaFondoConSupporti"
import DialogForm from "../../components/DialogForm"
import FondoForm from "../../components/forms/FondoForm"
import FondoFormContenuto from "../../components/forms/FondoFormContenuto"
import FondoFormNote from "../../components/forms/FondoFormNote"
import FondoFormChiavi from "../../components/forms/FondoFormChiavi"
import { Fondo, HistoryBag, WithAllegati } from "../../types"
import useUserCanWriteAndDelete, { useUserIsAdmin } from "../../hooks/permissions"
import { useIsMobile } from "../../hooks/mobile"
import AllegatiForm from "../../components/forms/AllegatiForm"
import AllegatiGrid from "../../components/AllegatiGrid"
import { useAuth } from "../../auth"
import { useMemo } from "react"

export default function FondoDettaglio() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const { data: fondo, refetch } = useFondo(parseInt(id as string))
  const navigate = useNavigate()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const userIsAdmin = useUserIsAdmin()

  const historyBag = useFondoHistory(id as string)

  const [updateModalState, updateModalActions] = useModalTrigger()
  const [updateContenutoModalState, updateContenutoModalActions] = useModalTrigger()
  const [updateNoteModalState, updateNoteModalActions] = useModalTrigger()
  const [updateChiaviModalState, updateChiaviModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()
  const [deleteConSupportiModalState, deleteConSupportiModalActions] = useModalTrigger()
  const [updateModalStateAllegati, updateModalActionsAllegati] = useModalTrigger()
  const { mutateAsync: remove } = useFondoDelete()

  const { mutateAsync: update } = useFondoUpdate()
  const { mutateAsync: setAllegati } = useFondoSetAllegati(id as string)

  const showMessage = useSnackbarMessage()

  const { ref } = useDesiredHeight(24)
  const isMobile = useIsMobile()

  const { user } = useAuth()

  const fondiCollegati = useMemo(() => {
    return (fondo?.fondi_collegati_data || []).map(item => item.fondo_collegato_data?.segnatura).join(", ")
  }, [fondo?.fondi_collegati_data])

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          overflow: "auto",
          height: "100%",
          p: 3,
          pt: 2,
        }}
      >
        <Grid
          size={{
            xs: 12,
            xl: 6,
          }}
        >
          <Stack
            sx={{
              height: "fit-content",
              bgcolor: "white",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle
              first
              title={t("sections.identificazione")}
              onEdit={() => {
                updateModalActions.open()
              }}
            />
            <Box px={3} pb={2}>
              <DataRow>
                <DataField label={t("fondo.denominazione")} value={fondo?.denominazione} size={6} />
                <DataField label={t("fondo.tipologia")} value={fondo?.tipologia_data?.termine} size={6} />
              </DataRow>
              <DataRow>
                <DataField label={t("fondo.genere")} value={fondo?.genere_data?.map((item) => item.termine)} size={8} />
              </DataRow>

              <DataRow>
                <DataField
                  label={t("fondo.produttore")}
                  value={joinNomeCognome(fondo?.produttore_data?.nome, fondo?.produttore_data?.cognome)}
                  size={6}
                  link={`/produttori/${fondo?.produttore}`}
                />
                <DataField label={t("fondo.segnatura")} value={fondo?.segnatura} size={6} />
              </DataRow>

              <DataRow>
                <DataField
                  label={t("fondo.donatore")}
                  value={joinNomeCognome(fondo?.donatore_data?.nome, fondo?.donatore_data?.cognome)}
                  size={6}
                  link={user?.ruolo !== "consultazione" ? `/donatori/${fondo?.donatore}` : undefined}
                />
                <DataField label={t("fondo.tipo_supporto")} value={fondo?.tipi_supporto_data?.map((item) => item.termine)} size={6} />
              </DataRow>

              <DataRow>
                <DataField label={t("fondo.descrizione")} value={fondo?.descrizione} size={{ xs: 12, xl: 8 }} />
              </DataRow>

              <DataRow>
                <DataField
                  label={t("fondo.conservatore_originale")}
                  value={fondo?.conservatore_originale_data?.nome}
                  size={6}
                />
                <DataField
                  label={t("fondo.conservatore_digitale")}
                  value={fondo?.conservatore_digitale_data?.nome}
                  size={6}
                />
              </DataRow>

              <DataRow>
                <DataField label={t("fondo.collocazione")} value={fondo?.collocazione} size={6} />
                <DataField label={t("fondo.collocazione_temporanea")} value={fondo?.collocazione_temporanea} size={6} />
              </DataRow>

              <DataRow>
                <DataField label={t("fondo.cronologia_da")} value={fondo?.cronologia_da} size={6} />
                <DataField label={t("fondo.cronologia_a")} value={fondo?.cronologia_a} size={6} />
              </DataRow>
              <DataRow>
                <DataField label={t("fondo.durata_totale")} value={fondo?.durata_totale} size={6} />
                <DataField label={t("fondo.durata_presunta")} value={fondo?.durata_presunta} size={6} />
              </DataRow>
              <DataRow>
                <DataField label={t("fondo.cronologia_note")} value={fondo?.cronologia_note} size={8} />
              </DataRow>

              <DataRow>
                <DataField label={t("fondo.fondi_collegati")} value={fondiCollegati} size={12} />
              </DataRow>

              <DataRow last>
                {/* <DataField label={t("fondo.provenienza")} value={fondo?.provenienza_data?.termine} size={6} /> */}
                <DataField label={t("fondo.provenienza")} value={(fondo?.luoghi_provenienza_data || []).map(item => item.nome).join(", ")} size={6}></DataField>
                <DataField boolean label={t("fondo.notifica")} value={fondo?.notifica} size={6} />
              </DataRow>
            </Box>

            {user?.ruolo !== "consultazione" && (
              <>
                <SectionTitle
                  title={t("sections.inventario_archivistico_contenuto_acquisizione")}
                  onEdit={() => {
                    updateContenutoModalActions.open()
                  }}
                />
                <Box px={3} pb={2}>
                  <DataRow>
                    <DataField
                      label={t("fondo.criteri_ordinamento")}
                      value={fondo?.criteri_ordinamento}
                      size={{ xs: 12, xl: 8 }}
                    />
                  </DataRow>
                  <DataRow last>
                    <DataField
                      label={t("fondo.modalita_acquisizione")}
                      value={fondo?.modo_acquisizione_data?.termine}
                      size={6}
                    />
                    <DataField
                      label={t("fondo.tipo_acquisizione")}
                      value={fondo?.tipo_acquisizione_data?.termine}
                      size={6}
                    />
                  </DataRow>
                  <DataRow>
                    <DataField
                      label={t("fondo.note_tipo_acquisizione")}
                      value={fondo?.note_tipo_acquisizione}
                      size={{ xs: 12, xl: 8 }}
                    />
                  </DataRow>

                  <DataRow>
                    <DataField label={t("fondo.data_acquisizione")} value={fondo?.data_acquisizione} size={6} />
                    <DataField label={t("fondo.consegna")} value={fondo?.consegna_data?.termine} size={6} />
                  </DataRow>

                  <DataRow>
                    <DataField
                      label={t("fondo.materiali_suppl_consegnati")}
                      value={fondo?.materiali_suppl_consegnati}
                      size={12}
                    />
                  </DataRow>

                  <DataRow>
                    <DataField
                      label={t("fondo.materiali_suppl_esistenti")}
                      value={fondo?.materiali_suppl_esistenti}
                      size={12}
                    />
                  </DataRow>

                  <DataRow>
                    <DataField
                      label={t("fondo.storia_archivistica")}
                      value={fondo?.storia_archivistica}
                      size={{ xs: 12, xl: 8 }}
                    />
                  </DataRow>

                  <DataRow>
                    <DataField
                      label={t("fondo.stato_conservazione")}
                      value={fondo?.stato_conservazione_data?.termine}
                      size={4}
                    />
                  </DataRow>
                  <DataRow>
                    <DataField
                      label={t("fondo.note_stato_conservazione")}
                      value={fondo?.note_stato_conservazione}
                      size={12}
                    />
                  </DataRow>

                  <DataRow>
                    <DataField boolean label={t("fondo.contributo")} value={fondo?.contributo} size={6} />
                  </DataRow>
                </Box>
              </>
            )}

            {user?.ruolo !== "consultazione" && (
              <>
                <SectionTitle
                  title={t("sections.inventario_archivistico_note_compilazione")}
                  onEdit={() => {
                    updateNoteModalActions.open()
                  }}
                />
                <Box px={3} pb={2}>
                  <DataRow>
                    <DataField boolean label={t("fondo.rel_archivistiche")} value={fondo?.rel_archivistiche} size={6} />
                  </DataRow>
                  <DataRow>
                    <DataField
                      label={t("fondo.note_rel_archivistiche")}
                      value={fondo?.note_rel_archivistiche}
                      size={{ xs: 12, xl: 8 }}
                    />
                  </DataRow>
                  <DataRow>
                    <DataField label={t("fondo.bibliografia")} value={fondo?.bibliografia} size={{ xs: 12, xl: 8 }} />
                  </DataRow>
                  <DataRow>
                    <DataField label={t("fondo.note")} value={fondo?.note} size={{ xs: 12, xl: 8 }} />
                  </DataRow>
                  <DataRow>
                    <DataField
                      label={t("fondo.note_archivista")}
                      value={fondo?.note_archivista}
                      size={{ xs: 12, xl: 8 }}
                    />
                  </DataRow>
                  <DataRow last>
                    <DataField
                      boolean
                      label={t("fondo.documentazione_completa")}
                      value={fondo?.documentazione_completa}
                      size={6}
                    />
                  </DataRow>
                </Box>
              </>
            )}

            <SectionTitle
              first
              title={t("sections.allegati")}
              onEdit={() => {
                updateModalActionsAllegati.open()
              }}
            />
            <Box px={3} py={2}>
              <AllegatiGrid allegati_data={fondo?.allegati_data || []} />
            </Box>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            xl: 6,
          }}
        >
          <Stack
            flexGrow={1}
            sx={{
              height: "100%",
              bgcolor: "white",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle
              first
              title={t("sections.catalogazione_chiavi_accesso")}
              onEdit={() => {
                updateChiaviModalActions.open()
              }}
            />
            <Box px={3} pb={2}>
              <ChiaviAccessoDisplay item={fondo} />
            </Box>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            xl: 12,
          }}
        >
          <Stack
            ref={ref}
            sx={{
              bgcolor: "grey.200",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle bgcolor="grey.700" info />
            <Stack sx={{ p: 2 }} flexGrow={1}>
              <RecordInfo
                first_update={fondo?.first_update}
                last_update={fondo?.last_update}
                historyBag={historyBag as HistoryBag}
              />
            </Stack>
            {userCanWriteAndDelete && !isMobile && fondo?.can_delete && (
              <DeleteBar title={t("fondo.cta_elimina")} onDelete={() => deleteModalActions.open(fondo)} />
            )}
            {userIsAdmin && !isMobile && fondo && !fondo.can_delete && (
              <DeleteBar
                title={t("fondo.cta_elimina_con_supporti")}
                onDelete={() => deleteConSupportiModalActions.open(fondo)}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
      <DialogForm<Fondo>
        open={updateModalState.isOpen}
        handleClose={updateModalActions.close}
        FormComponent={FondoForm}
        hasDoubleLanguage
        title={t("fondo.cta_modifica")}
        save={(values) => {
          return update(values).then(() => {
            updateModalActions.close()
            refetch()
            showMessage(t("fondo.esito_modifica_ok"))
          })
        }}
        item={fondo}
        hasCancel
      />
      <DialogForm<Fondo>
        open={updateContenutoModalState.isOpen}
        handleClose={updateContenutoModalActions.close}
        hasDoubleLanguage
        FormComponent={FondoFormContenuto}
        title={t("fondo.cta_modifica_contenuto_acquisizione")}
        save={(values) => {
          return update(values).then(() => {
            updateContenutoModalActions.close()
            refetch()
            showMessage(t("fondo.esito_modifica_ok"))
          })
        }}
        item={fondo}
        hasCancel
      />
      <DialogForm<Fondo>
        open={updateNoteModalState.isOpen}
        handleClose={updateNoteModalActions.close}
        FormComponent={FondoFormNote}
        hasDoubleLanguage
        title={t("fondo.cta_modifica_note_compilazione")}
        save={(values) => {
          return update(values).then(() => {
            updateNoteModalActions.close()
            refetch()
            showMessage(t("fondo.esito_modifica_ok"))
          })
        }}
        item={fondo}
        hasCancel
      />

      <DialogForm<Fondo>
        open={updateChiaviModalState.isOpen}
        handleClose={updateChiaviModalActions.close}
        FormComponent={FondoFormChiavi}
        title={t("fondo.cta_modifica_chiavi")}
        save={(values) => {
          return update(values).then(() => {
            updateChiaviModalActions.close()
            refetch()
            showMessage(t("fondo.esito_modifica_ok"))
          })
        }}
        item={fondo}
        hasCancel
      />

      <DialogForm<WithAllegati>
        open={updateModalStateAllegati.isOpen}
        handleClose={updateModalActionsAllegati.close}
        FormComponent={AllegatiForm}
        title={t("general.cta_modifica_allegati")}
        save={(values) => {
          const allegatiData = (values.allegati_data || []).map((a) => {
            return {
              id: a.id,
              note: a.note,
              attachment: typeof a.attachment === "string" ? undefined : a.attachment,
            }
          })
          return setAllegati({ allegati: allegatiData }).then(() => {
            updateModalActionsAllegati.close()
            refetch()
            showMessage(t("supporto.esito_modifica_ok"))
          })
        }}
        item={{ allegati_data: fondo?.allegati_data }}
        hasCancel
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("fondo.cta_elimina")}
        subtitle={
          <span>
            {t("fondo.cta_subtitle_elimina")} <b>"{deleteModalState.value?.denominazione}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("fondo.esito_elimina_ok"))
              navigate(`/${i18n.language}/fondi`)
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
      <DialogEliminaFondoConSupporti
        open={deleteConSupportiModalState.isOpen}
        handleClose={deleteConSupportiModalActions.close}
        fondo={deleteConSupportiModalState.value}
        onDeleted={() => navigate(`/${i18n.language}/fondi`)}
      />
    </>
  )
}
